import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { updateOrgAuth, type AuthConfig, type AuthMode } from '../orgs/api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { AuthFlowDialog } from './AuthFlowDialog';

function inferMode(cfg: AuthConfig): AuthMode {
  if (cfg.mode === 'oidc' || cfg.mode === 'sigil') return cfg.mode;
  return cfg.issuer ? 'oidc' : 'sigil';
}

export function AuthenticationPage() {
  const { org } = useCurrentOrg();
  const qc = useQueryClient();
  const orgId = org?.id ?? '';

  const [mode, setMode] = useState<AuthMode>(org ? inferMode(org.auth_config) : 'sigil');
  const [issuer, setIssuer] = useState(org?.auth_config.issuer ?? '');
  const [audience, setAudience] = useState(org?.auth_config.audience ?? '');
  const [userIdClaim, setUserIdClaim] = useState(org?.auth_config.user_id_claim ?? '');
  const [emailClaim, setEmailClaim] = useState(org?.auth_config.email_claim ?? '');
  const [discoveryDocumentJson, setDiscoveryDocumentJson] = useState(
    org?.auth_config.discovery_document_json ?? '',
  );
  const [useManualDiscovery, setUseManualDiscovery] = useState(
    !!org?.auth_config.discovery_document_json,
  );
  const [showAdvanced, setShowAdvanced] = useState(
    !!(org?.auth_config.user_id_claim || org?.auth_config.email_claim),
  );
  const [explainerOpen, setExplainerOpen] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const auth_config: AuthConfig =
        mode === 'oidc'
          ? {
              mode: 'oidc',
              type: 'oidc',
              issuer: issuer.trim() || undefined,
              audience: audience.trim(),
              user_id_claim: userIdClaim.trim() || undefined,
              email_claim: emailClaim.trim() || undefined,
              discovery_document_json: useManualDiscovery
                ? discoveryDocumentJson.trim() || undefined
                : undefined,
            }
          : { mode: 'sigil' };
      return updateOrgAuth(orgId, { auth_config });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs'] }),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  if (!org) return null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Authentication</h2>
        <p className="mt-1 text-sm text-ink-secondary">
          Choose how end users sign in to access their wallet.{' '}
          <button
            type="button"
            onClick={() => setExplainerOpen(true)}
            className="font-medium text-ink-primary underline-offset-4 hover:underline"
          >
            How does this work?
          </button>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader title="Authentication method" />
          <CardBody>
            <div className="grid gap-3 sm:grid-cols-2">
              <ModeOption
                title="Sigil hosted"
                tag="default"
                body="Email OTP, no setup. The wallet itself is the user identity."
                selected={mode === 'sigil'}
                onSelect={() => setMode('sigil')}
              />
              <ModeOption
                title="Bring your own"
                tag="OIDC"
                body="Plug in Keycloak, Auth0, Cognito, Firebase Auth, anything OIDC-compliant."
                selected={mode === 'oidc'}
                onSelect={() => setMode('oidc')}
              />
            </div>
          </CardBody>
        </Card>

        {mode === 'sigil' && <SigilHostedCard />}
        {mode === 'oidc' && (
          <OidcConfigCard
            issuer={issuer}
            setIssuer={setIssuer}
            audience={audience}
            setAudience={setAudience}
            userIdClaim={userIdClaim}
            setUserIdClaim={setUserIdClaim}
            emailClaim={emailClaim}
            setEmailClaim={setEmailClaim}
            showAdvanced={showAdvanced}
            setShowAdvanced={setShowAdvanced}
            useManualDiscovery={useManualDiscovery}
            setUseManualDiscovery={setUseManualDiscovery}
            discoveryDocumentJson={discoveryDocumentJson}
            setDiscoveryDocumentJson={setDiscoveryDocumentJson}
          />
        )}

        <div className="flex items-center justify-end gap-4">
          {save.isError && (
            <p className="text-sm text-bad">
              {save.error instanceof Error ? save.error.message : 'Could not save'}
            </p>
          )}
          {save.isSuccess && <p className="text-sm text-ok">Saved.</p>}
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>

      <AuthFlowDialog open={explainerOpen} onClose={() => setExplainerOpen(false)} />
    </div>
  );
}

function ModeOption({
  title,
  tag,
  body,
  selected,
  onSelect,
}: {
  title: string;
  tag: string;
  body: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`rounded-md border p-4 text-left transition-colors ${
        selected
          ? 'border-ink-primary ring-1 ring-ink-primary'
          : 'border-line-soft hover:border-line'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-ink-primary">{title}</span>
        <span className="rounded bg-surface-soft px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-ink-secondary">
          {tag}
        </span>
      </div>
      <p className="mt-2 text-xs text-ink-secondary">{body}</p>
    </button>
  );
}

function SigilHostedCard() {
  return (
    <Card>
      <CardHeader title="Sigil hosted authentication" description="No external provider needed." />
      <CardBody>
        <p className="text-sm text-ink-secondary">
          When your app embeds the wallet iframe, your end users sign in directly inside it with
          email + one-time code. The wallet is created on first sign-in and recovered on subsequent
          ones — the wallet itself is the user's identity.
        </p>
        <p className="mt-3 text-sm text-ink-secondary">
          End-user identities are scoped to this company. They are stored separately from this
          portal's accounts and never mix with other companies on Sigil.
        </p>
      </CardBody>
    </Card>
  );
}

function OidcConfigCard({
  issuer,
  setIssuer,
  audience,
  setAudience,
  userIdClaim,
  setUserIdClaim,
  emailClaim,
  setEmailClaim,
  showAdvanced,
  setShowAdvanced,
  useManualDiscovery,
  setUseManualDiscovery,
  discoveryDocumentJson,
  setDiscoveryDocumentJson,
}: {
  issuer: string;
  setIssuer: (v: string) => void;
  audience: string;
  setAudience: (v: string) => void;
  userIdClaim: string;
  setUserIdClaim: (v: string) => void;
  emailClaim: string;
  setEmailClaim: (v: string) => void;
  showAdvanced: boolean;
  setShowAdvanced: (v: boolean) => void;
  useManualDiscovery: boolean;
  setUseManualDiscovery: (v: boolean) => void;
  discoveryDocumentJson: string;
  setDiscoveryDocumentJson: (v: string) => void;
}) {
  const parsed = useManualDiscovery
    ? safeParseDiscovery(discoveryDocumentJson)
    : null;

  return (
    <Card>
      <CardHeader
        title="OIDC provider"
        description={
          useManualDiscovery
            ? 'Sigil reads issuer and jwks_uri from the pasted document. You own keeping it in sync with your IdP.'
            : 'JWKS is auto-discovered from your Issuer via /.well-known/openid-configuration.'
        }
      />
      <CardBody>
        <div className="space-y-5">
          {!useManualDiscovery && (
            <Input
              label="Issuer URL"
              placeholder="https://auth.example.com/realms/myrealm"
              value={issuer}
              onChange={(e) => setIssuer(e.target.value)}
              required
            />
          )}
          <Input
            label="Audience"
            placeholder="my-app"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            hint="The aud claim Sigil will require in incoming JWTs."
            required
          />

          <div className="rounded-md border border-line-soft bg-surface-soft p-4">
            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={useManualDiscovery}
                onChange={(e) => setUseManualDiscovery(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-line"
              />
              <span>
                <span className="font-medium text-ink-primary">
                  My provider doesn't publish /.well-known/openid-configuration
                </span>
                <span className="mt-1 block text-xs text-ink-secondary">
                  Paste the OpenID Provider Metadata document below. Sigil will skip
                  network discovery and use the values inside. The <code>jwks_uri</code>{' '}
                  in the document still needs to be reachable from Sigil's backend.
                </span>
              </span>
            </label>

            {useManualDiscovery && (
              <div className="mt-4 space-y-3">
                <textarea
                  value={discoveryDocumentJson}
                  onChange={(e) => setDiscoveryDocumentJson(e.target.value)}
                  rows={10}
                  spellCheck={false}
                  placeholder={discoveryPlaceholder}
                  className="w-full rounded-md border border-line bg-surface-white p-3 font-mono text-xs text-ink-primary placeholder:text-ink-tertiary focus:border-ink-primary focus:outline-none"
                />
                {parsed && 'error' in parsed && (
                  <p className="text-xs text-bad">{parsed.error}</p>
                )}
                {parsed && 'ok' in parsed && (
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
                    <dt className="text-ink-tertiary">Issuer</dt>
                    <dd className="font-mono text-ink-primary">{parsed.ok.issuer}</dd>
                    <dt className="text-ink-tertiary">JWKS URI</dt>
                    <dd className="font-mono text-ink-primary">{parsed.ok.jwks_uri}</dd>
                    {parsed.ok.algorithms && (
                      <>
                        <dt className="text-ink-tertiary">Algorithms</dt>
                        <dd className="font-mono text-ink-primary">
                          {parsed.ok.algorithms.join(', ')}
                        </dd>
                      </>
                    )}
                  </dl>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm font-medium text-ink-secondary underline-offset-4 hover:underline"
          >
            {showAdvanced ? 'Hide claim mapping' : 'Show claim mapping'}
          </button>

          {showAdvanced && (
            <div className="grid gap-5 border-t border-line-soft pt-5 sm:grid-cols-2">
              <Input
                label="User ID claim"
                value={userIdClaim}
                onChange={(e) => setUserIdClaim(e.target.value)}
                placeholder="sub"
                hint="Default: sub. Override only if your provider uses a non-standard claim."
              />
              <Input
                label="Email claim"
                value={emailClaim}
                onChange={(e) => setEmailClaim(e.target.value)}
                placeholder="email"
                hint="Default: email."
              />
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

const discoveryPlaceholder = `{
  "issuer": "https://auth.example.com",
  "authorization_endpoint": "https://auth.example.com/oauth/authorize",
  "token_endpoint": "https://auth.example.com/oauth/token",
  "jwks_uri": "https://auth.example.com/.well-known/jwks.json",
  "id_token_signing_alg_values_supported": ["RS256"]
}`;

// safeParseDiscovery is the client-side preview of what the backend will
// extract from the pasted document. It surfaces the two fields a
// misconfigured paste would silently swallow — issuer and jwks_uri —
// before the integrator hits Save and finds out at first-login time.
function safeParseDiscovery(raw: string):
  | null
  | { ok: { issuer: string; jwks_uri: string; algorithms?: string[] } }
  | { error: string } {
  const text = raw.trim();
  if (!text) return null;
  let doc: unknown;
  try {
    doc = JSON.parse(text);
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'invalid JSON' };
  }
  if (typeof doc !== 'object' || doc === null) {
    return { error: 'Document must be a JSON object' };
  }
  const obj = doc as Record<string, unknown>;
  const issuer = typeof obj.issuer === 'string' ? obj.issuer : '';
  const jwks_uri = typeof obj.jwks_uri === 'string' ? obj.jwks_uri : '';
  if (!issuer) return { error: 'Missing required field: issuer' };
  if (!jwks_uri) return { error: 'Missing required field: jwks_uri' };
  const algs = obj.id_token_signing_alg_values_supported;
  const algorithms =
    Array.isArray(algs) && algs.every((x) => typeof x === 'string') ? (algs as string[]) : undefined;
  return { ok: { issuer, jwks_uri, algorithms } };
}

